import { MailFilled } from '@ant-design/icons';
import { Button, Table } from 'antd';
import { useEffect, useRef, useState } from 'react';
import ContactForSupport from '../components/ContactForSupport';
import PropTypes from 'prop-types';
import { useTransactionContext } from '@/context/TransactionContext';
import { getBsFromAD } from '../helpers/getTodayDate';
import DynamicTableSkeleton from './DynamicTableSkeleton';
import CustomPagination from '../pagination/CustomPagination';
import useInfiniteTableScroll from '@/hooks/useInfiniteTableScroll';

/**
 * DynamicTable Component
 *
 * Renders a dynamic table using Ant Design Table component.
 * @param {Object} props - Component props
 * @param {Array} props.data - Array of data objects to display in the table
 * @param {Array} props.columns - Array of column definitions for the table
 * @returns {JSX.Element} - Rendered DynamicTable component
 */
export default function DynamicTable({
  data,
  columns,
  loading,
  pagination,
  isShowDynamicScroll,
  pageNumber,
  pageSize,
  setPageNumber,
  metaData,
  setPageSize,
  onLoadMore,
  hasMore = false,
  loadingMore = false,
  ...rest
}) {
  const [drawerVisible, setDrawerVisible] = useState(false);
  const tableWrapRef = useRef(null);
  const isInfinite = typeof onLoadMore === 'function';

  useInfiniteTableScroll({
    containerRef: tableWrapRef,
    enabled: isInfinite && !loading,
    hasMore: isInfinite && hasMore,
    loading: loading || loadingMore,
    onLoadMore,
  });
  const { dateTypeList } = useTransactionContext();
  const [scrollY, setScrollY] = useState(200); // Default height

  const showDrawer = () => {
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
  };

  const modifiedColumns = columns?.map((item) => {
    if (item?.key?.toLowerCase().includes('date')) {
      return {
        ...item,
        render: (text) => {
          return (
            <>{dateTypeList === 'bs' ? text && getBsFromAD(text) : text}</>
          );
        },
      };
    }
    if (item?.dataIndex === 'amount') {
      return {
        ...item,
        render: (text, record) => {
          return <>{record?.isCancel ? '' : text}</>;
        },
      };
    }
    return item;
  });

  useEffect(() => {
    // Function to calculate dynamic table height
    const updateTableHeight = () => {
      const windowHeight = window.innerHeight;
      const tableOffset = 320; // Adjust based on header, padding, etc.

      const updatedHeight = Math.max(windowHeight - tableOffset, 100);
      setScrollY(updatedHeight);
    };
    updateTableHeight(); // Initial height set
    window.addEventListener('resize', updateTableHeight); // Update on resize
    return () => window.removeEventListener('resize', updateTableHeight);
  }, []);

  return loading ? (
    // skeleton loader
    <DynamicTableSkeleton loading={loading} columns={columns} />
  ) : (
    <div style={{ padding: '15px' }} ref={tableWrapRef}>
      <Table
        size="small"
        bordered
        tableLayout="auto"
        onHeaderRow={() => ({
          style: { whiteSpace: 'nowrap' },
        })}
        rowClassName={(record) => (record?.isCancel ? 'tw-bg-[yellow]' : '')}
        columns={modifiedColumns}
        scroll={isShowDynamicScroll ? { y: scrollY, x: true } : { x: true }}
        dataSource={data?.map((item) => ({
          ...item,
          key: item?.referenceId,
        }))}
        loading={loading}
        // locale={{
        //   emptyText: loading ? (
        //     // <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-10">
        //     //   <ClockLoader color="#10a96ff1" />
        //     // </div>
        //     <div className="tw-flex tw-flex-col tw-items-center tw-justify-center">
        //       <ClockLoader color="#10a96ff1" />
        //       <div className="tw-text-green-600 tw-mt-2 tw-font-medium">
        //         Loading, please wait...
        //       </div>
        //     </div>
        //   ) : (
        //     <Empty
        //       styles={{ image: { height: 60 } }}
        //       description={<Typography.Text>No Records Found</Typography.Text>}
        //     ></Empty>
        //   ),
        // }}
        // pagination={scrollY ? false : pagination}
        pagination={false}
        footer={() => (
          <div
            style={{
              float: 'left',
              marginTop: '20px',
              background: 'none',
            }}
          >
            <Button
              onClick={showDrawer}
              type="link"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              <MailFilled />
              Contact for Support
            </Button>
            <ContactForSupport visible={drawerVisible} onClose={closeDrawer} />
          </div>
        )}
      />
      {isInfinite && (loadingMore || metaData?.total_records > 0) && (
        <div
          className="tw-flex tw-justify-end tw-items-center tw-gap-2 tw-text-xs tw-text-gray-500"
          style={{
            flexShrink: 0,
            padding: '8px 16px',
            borderTop: '1px solid #f0f0f0',
            background: '#fff',
          }}
        >
          {`Showing ${data?.length ?? 0} of ${metaData?.total_records ?? 0}`}
        </div>
      )}
      {!isInfinite && pageNumber && (
        <div
          className="tw-flex tw-justify-end tw-items-center tw-px-4 tw-bg-inherit tw-border-t tw-border-gray-200"
          style={{
            flexShrink: 0,
            padding: '12px 16px',
            borderTop: '1px solid #f0f0f0',
            background: '#fff',
          }}
        >
          <CustomPagination
            pageNumber={pageNumber}
            pageSize={pageSize}
            setPageNumber={setPageNumber}
            setPageSize={setPageSize}
            metaData={metaData}
            loading={loading}
          />
        </div>
      )}
      {/* </Loader> */}
    </div>
  );
}

DynamicTable.propTypes = {
  pagination: PropTypes.bool,
  scrollY: PropTypes.bool,
  onLoadMore: PropTypes.func,
  hasMore: PropTypes.bool,
  loadingMore: PropTypes.bool,
};

DynamicTable.defaultProps = {
  pagination: true,
  scrollY: false,
};
